import { useCallback, useState } from "react";
import { useAuth } from "../context/AuthContext";

const ELEVATION_WINDOW_MS = 5 * 60_000; // re-auth stays "fresh" for 5 minutes of admin actions

/**
 * Every admin action is supposed to require MFA re-authentication. B2C's
 * step-up policy is a full-page redirect, so there's no way to seamlessly
 * "pause a click, verify MFA, resume the click" -- the honest version of
 * this requirement is: block the action behind a confirmation step, and if
 * the admin hasn't re-authenticated (verified via AuthContext checking the
 * B2C policy claim on the ID token, not just "a redirect happened") within
 * the last few minutes, send them through step-up first. They'll need to
 * re-click the action after returning -- there is no in-memory state left
 * to resume automatically across a full-page redirect.
 *
 * GAP: real enforcement belongs server-side in
 * api/security/session_manager.py's enforce_mfa_if_needed, which today only
 * triggers step-up for a new/untrusted device or 30+ days inactive, not
 * "every admin mutation." This hook is the client-side approximation until
 * that gets an admin-specific always-step-up rule.
 */
export function useRequireReauth() {
  const { stepUpMfa, lastReauthAt } = useAuth();
  const [pendingLabel, setPendingLabel] = useState(null);

  const isElevated = lastReauthAt && Date.now() - lastReauthAt < ELEVATION_WINDOW_MS;

  const requireReauth = useCallback(
    (actionLabel, onConfirmed) => {
      if (isElevated) {
        onConfirmed();
        return;
      }
      setPendingLabel(actionLabel);
    },
    [isElevated]
  );

  return {
    isElevated,
    pendingLabel,
    requireReauth,
    cancelReauth: () => setPendingLabel(null),
    startStepUp: () => stepUpMfa(),
  };
}
